import { useParams, Link } from "react-router-dom";
import ImageLightbox from "@/components/ImageLightbox";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import { Card, CardContent } from "@/components/ui/card";
import { GraduationCap, Heart, Briefcase, ArrowLeft, Lightbulb, Award, HandHeart, Linkedin } from "lucide-react";
import OpenAIIcon from "@/components/icons/OpenAIIcon";
import { Button } from "@/components/ui/button";
import { teamMembers, type TeamMember } from "@/data/teamMembers";
import MarqueeTicker from "@/components/MarqueeTicker";
import { FEATURE_FLAGS, CHATBOT_URL } from "@/config/featureFlags";

/* ── Detail profile card ── */
const ProfileCard = ({ member }: { member: TeamMember }) => (
  <article className="animate-in fade-in-50 duration-500 space-y-8">
    {/* Back link */}
    <Button variant="ghost" size="sm" asChild className="gap-1.5 text-muted-foreground">
      <Link to="/wer-wir-sind">
        <ArrowLeft className="h-4 w-4" />
        Zurück zum Team
      </Link>
    </Button>

    <Card className="overflow-hidden border-border/50 shadow-lg">
      <CardContent className="p-0">
        <div className="grid md:grid-cols-[300px_1fr] gap-0">
          {/* Photo */}
          <div className="relative bg-muted overflow-hidden">
            <ImageLightbox
              src={member.imageUrl}
              alt={member.name}
              className={`w-full h-72 md:h-full object-cover ${member.id === "stephan" ? "scale-[1.1] -translate-y-8 object-center" : "object-[center_15%]"} ${member.id === "margret" ? "scale-[1.8] origin-[42%_0%]" : ""} ${member.id === "burkhard" ? "scale-[1.2] origin-top object-[30%_15%]" : ""}`}
            />
          </div>

          {/* Header + Intro */}
          <div className="p-6 md:p-8 space-y-5">
            <div>
              <h1 className="text-2xl md:text-3xl font-serif font-bold text-foreground">
                {member.name}
              </h1>
              <p className="text-primary font-medium mt-1">{member.title}</p>
              <p className="text-muted-foreground text-sm mt-0.5">{member.focus}</p>
              {member.linkedinUrl && (
                <a
                  href={member.linkedinUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 transition-colors mt-2"
                >
                  <Linkedin className="h-4 w-4" />
                  LinkedIn Profil
                </a>
              )}
            </div>

            {/* Motto */}
            <blockquote className="bg-primary rounded-lg px-5 py-4 italic text-white">
              {`\u201E${member.motto}\u201C`}
            </blockquote>

            {/* Intro */}
            <p className="text-sm text-muted-foreground leading-relaxed">{member.intro}</p>

            {/* Haltung (Margret only) */}
            {member.haltung && (
              <div className="flex gap-3">
                <Lightbulb className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <div>
                  <h2 className="font-semibold text-sm text-foreground mb-1">Meine Haltung</h2>
                  <ul className="space-y-1">
                    {member.haltung.map((h) => (
                      <li key={h} className="text-sm text-muted-foreground">
                        {h}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>

    {/* Motivation */}
    <Card className="group border-border/50 shadow-md transition-all duration-300 hover:bg-primary hover:border-primary hover:shadow-lg">
      <CardContent className="p-6 md:p-8">
        <div className="flex gap-3">
          <Heart className="h-5 w-5 text-primary mt-0.5 shrink-0 transition-colors group-hover:text-white" />
          <div>
            <h2 className="font-semibold text-foreground mb-2 transition-colors group-hover:text-white">Motivation & Leidenschaft</h2>
            <p className="text-sm text-muted-foreground leading-relaxed transition-colors group-hover:text-white/90">{member.motivation}</p>
          </div>
        </div>
      </CardContent>
    </Card>

    {/* Schlüsselprojekte (Stephan) */}
    {member.schluesselProjekte && (
      <Card className="group border-border/50 shadow-md transition-all duration-300 hover:bg-primary hover:border-primary hover:shadow-lg">
        <CardContent className="p-6 md:p-8">
          <div className="flex gap-3">
            <Award className="h-5 w-5 text-primary mt-0.5 shrink-0 transition-colors group-hover:text-white" />
            <div>
              <h2 className="font-semibold text-foreground mb-2 transition-colors group-hover:text-white">Schlüsselprojekte</h2>
              <ul className="space-y-1">
                {member.schluesselProjekte.map((p) => (
                  <li key={p} className="text-sm text-muted-foreground transition-colors group-hover:text-white/90">
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    )}

    {/* Background */}
    <Card className="group border-border/50 shadow-md transition-all duration-300 hover:bg-primary hover:border-primary hover:shadow-lg">
      <CardContent className="p-6 md:p-8">
        <div className="flex gap-3">
          <Briefcase className="h-5 w-5 text-primary mt-0.5 shrink-0 transition-colors group-hover:text-white" />
          <div className="w-full">
            <h2 className="font-semibold text-foreground mb-4 transition-colors group-hover:text-white">{member.id === "stephan" ? "Meine Stationen" : "Hintergrund & Berufserfahrung"}</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {member.background.map((group) => (
                <div key={group.category}>
                  {member.id !== "stephan" && <h3 className="text-sm font-semibold text-primary mb-2 transition-colors group-hover:text-white">{group.category}</h3>}
                  <ul className="space-y-1">
                    {group.items.map((item) => (
                      <li key={item} className="text-sm text-muted-foreground transition-colors group-hover:text-white/90">
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>

    {/* Ehrenamt (Margret) */}
    {member.ehrenamt && (
      <Card className="group border-border/50 shadow-md transition-all duration-300 hover:bg-primary hover:border-primary hover:shadow-lg">
        <CardContent className="p-6 md:p-8">
          <div className="flex gap-3">
            <HandHeart className="h-5 w-5 text-primary mt-0.5 shrink-0 transition-colors group-hover:text-white" />
            <div>
              <h2 className="font-semibold text-foreground mb-2 transition-colors group-hover:text-white">Engagement & Publikationen</h2>
              <ul className="space-y-1">
                {member.ehrenamt.map((e) => (
                  <li key={e} className="text-sm text-muted-foreground transition-colors group-hover:text-white/90">
                    {e}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    )}

    {/* Expertise */}
    <Card className="border-border/50 shadow-md">
      <CardContent className="p-6 md:p-8">
        <div className="flex gap-3">
          <GraduationCap className="h-5 w-5 text-primary mt-0.5 shrink-0" />
          <div className="w-full">
            <h2 className="font-semibold text-foreground mb-4">Kompetenzen & Weiterbildungen</h2>
            <div className="space-y-4">
              {member.expertise.map((group, index) => {
                const offset = (index / member.expertise.length) * 100;
                const opacity = 0.12 + (index / (member.expertise.length - 1)) * 0.28;
                return (
                  <div
                    key={group.category}
                    className="rounded-lg px-4 py-3"
                    style={{ backgroundColor: `rgba(74, 162, 71, ${opacity})` }}
                  >
                    <h3
                      className="text-sm font-semibold text-primary mb-2"
                      style={{ marginLeft: `${offset}%` }}
                    >
                      {group.category}
                    </h3>
                    <MarqueeTicker items={group.items} />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  </article>
);

/* ── Page ── */
const WerWirSind = () => {
  const { memberId } = useParams<{ memberId?: string }>();
  const selectedMember = memberId
    ? teamMembers.find((m) => m.id === memberId)
    : undefined;

  return (
    <>
      <SEOHead title="Wer wir sind – Leadership Focus Team" description="Lernen Sie das Team von Leadership Focus kennen. Erfahrene Berater, Coaches und Supervisoren seit 2008 in München." canonical="https://leadership-focus.de/wer-wir-sind" />
      <Header />
      <main className="container py-16 md:py-20 min-h-[60vh]">
        {/* Intro */}
        <div className="mb-10">
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-foreground mb-4">
            Wer wir sind
          </h1>
          <p className="text-muted-foreground leading-relaxed">
            Leadership Focus ist seit der Gründung 2008 ein Familienunternehmen, das sich auf die
            Beratung von Menschen, Teams und Organisationen spezialisiert hat. Basierend auf unseren
            unterschiedlichen Kompetenzen unterstützen wir unsere Kunden in der persönlichen,
            interaktionalen und organisationalen Veränderungsarbeit.
          </p>
        </div>

        {selectedMember ? (
          <ProfileCard key={selectedMember.id} member={selectedMember} />
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            {teamMembers.map((m) => (
              <Link key={m.id} to={`/wer-wir-sind/${m.id}`}>
                <Card className="overflow-hidden border-border/50 shadow-md hover:shadow-lg hover:border-primary/30 transition-all duration-300 cursor-pointer group">
                  <CardContent className="p-0">
                    <div className="overflow-hidden">
                      <img src={m.teamImageUrl || m.imageUrl} alt={m.name} className="w-full h-64 object-cover object-[center_20%] group-hover:scale-105 transition-transform duration-500" />
                    </div>
                    <div className="p-5">
                      <h2 className="text-xl font-serif font-bold text-foreground">{m.name}</h2>
                      <p className="text-primary text-sm font-medium mt-1">{m.title}</p>
                      <p className="text-muted-foreground text-sm mt-2">{m.focus}</p>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}

            {/* KI-Teammitglied – hidden until launch */}
            {FEATURE_FLAGS.showChatBot && (
              <a href={CHATBOT_URL} target="_blank" rel="noopener noreferrer">
                <Card className="overflow-hidden border-border/50 shadow-md hover:shadow-lg hover:border-primary/30 transition-all duration-300 cursor-pointer group">
                  <CardContent className="p-0">
                    <div className="bg-primary/10 flex items-center justify-center h-64">
                      <OpenAIIcon className="h-20 w-20 text-primary group-hover:scale-110 transition-transform duration-500" />
                    </div>
                    <div className="p-5">
                      <h2 className="text-xl font-serif font-bold text-foreground">Leadership Focus Chat</h2>
                      <p className="text-primary text-sm font-medium mt-1">KI-basierte Unterstützung</p>
                      <p className="text-muted-foreground text-sm mt-2">Ihr digitaler Ansprechpartner rund um Leadership Focus</p>
                    </div>
                  </CardContent>
                </Card>
              </a>
            )}
          </div>
        )}
      </main>
      <Footer />
    </>
  );
};

export default WerWirSind;
